import { useMemo, useState } from 'react';
import { ChevronDown, PlusIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  useArchiveIcs221Entry,
  useCreateIcs221Entry,
  useIcs221Entries,
} from 'components/IAP/hooks/ics221EntryHooks';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { ICS204_SURFACE } from '../AssignmentList/assignmentCardStyles';
import { Ics221Form, type Ics221ResourceOption } from './Ics221Form';
import { ics221Rows, type Ics221Row as Ics221RowData } from './ics221Rows';

const COLUMN_COUNT = 4;

/**
 * One ICS 221 as a table row that opens to its numbered boxes, the way an ICS
 * 204 row opens to its assignment.
 */
function Ics221Row({
  row,
  expanded,
  onToggle,
  canEdit,
  resources,
  stampFirstVersion,
}: {
  row: Ics221RowData;
  expanded: boolean;
  onToggle: () => void;
  canEdit: boolean;
  resources: readonly Ics221ResourceOption[];
  stampFirstVersion: boolean;
}) {
  const archive = useArchiveIcs221Entry();

  return (
    <tbody data-testid="ics221-row">
      <TableRow
        data-testid="ics221-row-header"
        className="tw:cursor-pointer tw:bg-black tw:hover:bg-black"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell className="tw:w-8 tw:bg-black">
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:transition-transform',
              expanded && 'tw:rotate-180'
            )}
          />
        </TableCell>
        <TableCell className="tw:min-w-0 tw:bg-black tw:text-left">
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
            <span className="tw:truncate tw:font-medium">{row.label}</span>
            {row.detail ? (
              <span className="tw:truncate tw:text-xs tw:text-[var(--text-weak)]">
                {row.detail}
              </span>
            ) : null}
          </div>
        </TableCell>
        <TableCell className="tw:bg-black tw:text-left">
          <Badge variant={row.kind === 'entry' ? 'blue' : 'secondary'}>
            {row.kind === 'entry' ? 'Documentation entry' : 'From check-out'}
          </Badge>
        </TableCell>
        <TableCell
          className="tw:bg-black tw:text-right"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {row.kind === 'entry' && canEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Archive ${row.label}`}
              disabled={archive.isPending}
              onClick={() =>
                archive.mutate(row.id, {
                  onSuccess: () => toast.success('ICS 221 entry archived.'),
                  onError: () =>
                    toast.error('The entry could not be archived.'),
                })
              }
            >
              <Trash2Icon />
            </Button>
          ) : null}
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className="tw:hover:bg-transparent">
          <TableCell
            colSpan={COLUMN_COUNT}
            className="tw:h-auto tw:min-w-0 tw:whitespace-normal tw:p-0 tw:align-top"
          >
            <Ics221Form
              embedded
              title={`Demobilization Check-Out ICS-221: ${row.label}`}
              instance={
                row.kind === 'entry'
                  ? { kind: 'entry', entry: row.entry }
                  : {
                      kind: 'checkout',
                      resourceId: row.id,
                      overlay: row.overlay,
                    }
              }
              resources={resources}
              stampFirstVersion={stampFirstVersion}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </tbody>
  );
}

/**
 * Every ICS 221 on the incident: the sheets resource check-outs stamp and the
 * documentation entries the Demobilization Unit adds by hand.
 */
export function Ics221List() {
  const { readOnlyForms } = useICSContext();
  const { data: permissions } = useIncidentPermissions();
  const canEdit =
    !readOnlyForms && (permissions?.['incident:editCards'] ?? false);
  const { data: resources = [] } = useResourcesInIncident();
  const { data: entries = [] } = useIcs221Entries();
  const create = useCreateIcs221Entry();
  const rows = useMemo(
    () => ics221Rows(resources, entries),
    [resources, entries]
  );
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [createdId, setCreatedId] = useState('');
  const resourceOptions = useMemo(
    () =>
      resources.map((resource) => ({
        id: resource.resource_in_incident_id,
        label:
          resource.resource_name_identifier ?? resource.resource_in_incident_id,
      })),
    [resources]
  );

  const toggle = (id: string) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addEntry = () =>
    create.mutate(
      {},
      {
        onSuccess: (entry) => {
          setCreatedId(entry.id);
          setExpandedIds((current) => new Set(current).add(entry.id));
        },
        onError: () => toast.error('The ICS 221 could not be started.'),
      }
    );

  return (
    <div
      className="dark tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:text-foreground"
      data-tour="demob-ics221"
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <h3 className="tw:m-0 tw:text-sm tw:font-medium">
          ICS 221 Check-Outs ({rows.length})
        </h3>
        <Button
          type="button"
          size="sm"
          data-testid="ics221-add-entry"
          disabled={!canEdit || create.isPending}
          onClick={addEntry}
        >
          <PlusIcon />
          {create.isPending ? 'Starting…' : 'Add documentation entry'}
        </Button>
      </div>

      <div
        data-testid="ics221-table-wrapper"
        className={cn(
          'tw:w-full tw:min-w-0 tw:overflow-visible tw:rounded-lg tw:border tw:border-[var(--border-primary)]',
          'tw:[&>[data-slot=table-container]]:min-w-0 tw:[&>[data-slot=table-container]]:!overflow-visible',
          ICS204_SURFACE
        )}
      >
        <Table className={cn(ICS204_SURFACE, 'tw:w-full tw:min-w-0')}>
          <TableHeader>
            <TableRow className="tw:border-b tw:border-[var(--border-primary)] tw:hover:bg-transparent">
              <TableHead className={cn(ICS204_SURFACE, 'tw:w-8')}>
                <span className="tw:sr-only">Expand</span>
              </TableHead>
              <TableHead
                className={cn(ICS204_SURFACE, 'tw:text-[var(--text-weak)]')}
              >
                ICS 221
              </TableHead>
              <TableHead
                className={cn(ICS204_SURFACE, 'tw:text-[var(--text-weak)]')}
              >
                Type
              </TableHead>
              <TableHead className={ICS204_SURFACE}>
                <span className="tw:sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          {rows.map((row) => (
            <Ics221Row
              key={`${row.kind}-${row.id}`}
              row={row}
              expanded={expandedIds.has(row.id)}
              onToggle={() => toggle(row.id)}
              canEdit={canEdit}
              resources={resourceOptions}
              stampFirstVersion={row.id === createdId}
            />
          ))}
          {rows.length === 0 ? (
            <tbody>
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="tw:h-24 tw:text-center tw:font-normal tw:whitespace-normal tw:text-muted-foreground"
                >
                  No ICS 221s yet. Checking a resource out adds one, or add a
                  documentation entry by hand.
                </TableCell>
              </TableRow>
            </tbody>
          ) : null}
        </Table>
      </div>
    </div>
  );
}
